import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserZ, UserRole, userRoles } from '@libs/schemas';
import { Input } from '@/components/ui/input';
import { PatternFormat } from 'react-number-format';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { UserFormOrganizationAssignment } from './UserFormOrganiziationAssignment';
import { useToast } from 'src/components/useToast';
import { Tooltip } from '@/components/ui/tooltip';
import { HelpCircle, AlertCircle } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { z } from 'zod';
import { PHONE_REGEX, isValidPhoneNumber } from '@libs/schemas/src/utils/phone';

// Form-specific schema that's more lenient for editing users with legacy data
const userFormZ = z.object({
  phone: z
    .string()
    .min(1, 'Phone is required')
    .refine(
      (val) => {
        // Allow any value if field is untouched or empty during edit
        if (!val || val.length === 0) return true;
        // Otherwise validate format
        return PHONE_REGEX.test(val) && isValidPhoneNumber(val);
      },
      {
        message: 'Invalid phone number format. Must be +1 followed by 10 digits',
      },
    ),
  email: z.email('Invalid email address').min(1, 'Email is required'),
  name: z.object({
    first: z.string().min(1, 'First name is required'),
    last: z.string().min(1, 'Last name is required'),
  }),
  roles: z.array(z.enum(userRoles)),
  organizations: z.array(
    z
      .object({
        organizationId: z.string(),
        roles: z.array(z.enum(userRoles)),
      })
      .optional(),
  ),
});

export interface UserFormRef {
  submit: () => Promise<void>;
}

interface UserFormProps {
  onSubmit: (_value: UserZ) => void;
  onCancel: () => void;
  initialData?: Partial<UserZ>;
  isEditing?: boolean;
}

export const UserForm = forwardRef<UserFormRef, UserFormProps>(
  ({ onSubmit, onCancel: _onCancel, initialData, isEditing: _isEditing }, ref) => {
    const { errorToast } = useToast();
    const [isCustomer, setIsCustomer] = useState(initialData?.roles?.includes('customer') || false);

    const form = useForm<UserZ>({
      resolver: zodResolver(userFormZ),
      defaultValues: {
        email: initialData?.email || '',
        phone: initialData?.phone || '',
        name: {
          first: initialData?.name?.first || '',
          last: initialData?.name?.last || '',
        },
        roles: initialData?.roles || [],
        organizations: initialData?.organizations || [],
      },
      mode: 'onTouched', // Only validate fields after they've been touched
      criteriaMode: 'all',
    });

    // Update the validation schema when isCustomer changes
    useEffect(() => {
      form.clearErrors();
      // This triggers re-validation with the new schema
      form.trigger();
    }, [isCustomer, form]);

    // Watch for changes in the organizations field
    const organizations = useWatch({
      control: form.control,
      name: 'organizations',
    });

    // Infer global roles from organization roles and customer checkbox
    useEffect(() => {
      if (!organizations) return;

      // Extract all unique roles from all organizations
      const allOrgRoles = new Set<UserRole>();
      organizations.forEach((org) => {
        // Use optional chaining to safely access org and org.roles
        if (org?.roles) {
          org.roles.forEach((role) => {
            // Include all roles from organizations
            allOrgRoles.add(role);
          });
        }
      });

      // Prepare the global roles (preserving existing roles)
      const globalRoles: UserRole[] = [...allOrgRoles];

      // Add 'customer' role based on checkbox
      if (isCustomer && !globalRoles.includes('customer')) {
        globalRoles.push('customer');
      } else if (!isCustomer) {
        // Remove customer role if checkbox is unchecked
        const index = globalRoles.indexOf('customer');
        if (index > -1) {
          globalRoles.splice(index, 1);
        }
      }

      // Update the global roles field
      form.setValue('roles', globalRoles);
    }, [organizations, isCustomer, form]);

    const handleSubmit = (data: UserZ) => {
      // Ensure roles array exists
      if (!data.roles) {
        data.roles = [];
      }

      // Ensure organizations array exists and clean up undefined values
      if (!data.organizations) {
        data.organizations = [];
      } else {
        // Use type-safe filter with a proper type guard
        data.organizations = data.organizations.filter(
          (org): org is NonNullable<(typeof data.organizations)[number]> =>
            org !== undefined && org !== null,
        );
      }

      // Manage customer role based on checkbox
      if (isCustomer && !data.roles.includes('customer')) {
        data.roles.push('customer');
      } else if (!isCustomer) {
        // Remove customer role if checkbox is unchecked
        data.roles = data.roles.filter((role) => role !== 'customer');
      }

      // Ensure at least one role is selected (either customer or org roles)
      // If the user is a customer, they already have a role
      if (!isCustomer && !data.organizations.some((org) => org?.roles && org.roles.length > 0)) {
        errorToast({
          title: 'Role Required',
          description: 'User must either be a customer or have at least one organization role.',
        });
        return;
      }

      // If user is not a customer and has organizations, verify each org has roles
      if (data.organizations.length > 0) {
        // Use optional chaining for type safety
        const invalidOrgs = data.organizations.filter(
          (org) => !org?.roles || org.roles.length === 0,
        );
        if (invalidOrgs.length > 0) {
          errorToast({
            title: 'Roles Required',
            description: 'Please assign at least one role for each organization.',
          });
          return;
        }
      }

      // Submit the form with the valid data
      onSubmit(data);
    };

    // Expose submit function to parent component
    useImperativeHandle(ref, () => ({
      submit: async () => {
        const isValid = await form.trigger();
        if (isValid) {
          const values = form.getValues();
          handleSubmit(values);
        }
      },
    }));

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8" id="user-form">
          {/* User Information Section */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">User Information</h3>
              <p className="text-sm text-muted">Basic user information</p>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name.first"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter first name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name.last"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-muted">Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter email address" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-muted">Phone</FormLabel>
                    <FormControl>
                      <PatternFormat
                        placeholder="+1 (305)-123-4567"
                        value={field.value ? field.value.replace('+1', '') : ''}
                        onValueChange={(values) => {
                          field.onChange('+1' + values.value);
                        }}
                        onBlur={field.onBlur}
                        className={cn(
                          'flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                        )}
                        format="+1 (###)-###-####"
                        customInput={Input}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is-customer"
                  checked={isCustomer}
                  onCheckedChange={(checked) => setIsCustomer(checked === true)}
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <label
                  htmlFor="is-customer"
                  className="text-sm font-medium text-muted leading-none cursor-pointer"
                >
                  This user is a customer
                </label>
                <Tooltip content="If checked, this user will have the 'customer' role globally.">
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>
            </div>
          </div>

          <Separator />

          {/* Organization Assignment Section */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Organization Assignment</h3>
              <p className="text-sm text-muted">Assign organizations and roles to this user</p>
            </div>
            <UserFormOrganizationAssignment form={form} />
          </div>

          {/* Role validation alert - only show if neither customer nor org roles are selected */}
          {!isCustomer &&
            (!organizations ||
              !organizations.some((org) => org?.roles && org.roles.length > 0)) && (
              <Alert
                variant="destructive"
                className="bg-destructive/10 text-destructive border-destructive/20"
              >
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please either check "This user is a customer" or assign at least one role to an
                  organization.
                </AlertDescription>
              </Alert>
            )}

          {/* Hidden input to store global roles derived from org roles */}
          <Controller
            control={form.control}
            name="roles"
            render={({ field }) => (
              <input type="hidden" {...field} value={JSON.stringify(field.value)} />
            )}
          />
        </form>
      </Form>
    );
  },
);

UserForm.displayName = 'UserForm';
